import React, { useState, useEffect } from 'react';
import { samlAPI } from '../services/api';
import { PageLayout } from './ui';

const CertificatesPage = () => {
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newCert, setNewCert] = useState({
    name: '',
    common_name: '',
    organization: 'IDP',
    validity_days: 365
  });

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    try {
      setLoading(true);
      const res = await samlAPI.listCertificates();
      setCertificates(res.certificates || []);
    } catch (err) {
      setError(err.message || 'Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCertificate = async () => {
    try {
      setLoading(true);
      setError('');
      await samlAPI.createCertificate({
        ...newCert,
        generate: true
      });
      setSuccess('Certificate generated successfully');
      setNewCert({
        name: '',
        common_name: '',
        organization: 'IDP',
        validity_days: 365
      });
      loadCertificates();
    } catch (err) {
      setError(err.message || 'Failed to generate certificate');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCertificate = async (certId, certName, type = 'public', password = null) => {
    try {
      const blob = await samlAPI.downloadCertificate(certId, type, password);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      let filename;
      if (type === 'pfx') {
        filename = `${certName.replace(/ /g, '_')}.pfx`;
      } else if (type === 'private') {
        filename = `${certName.replace(/ /g, '_')}_private_key.pem`;
      } else {
        filename = `${certName.replace(/ /g, '_')}_certificate.pem`;
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      const typeLabel = type === 'pfx' ? 'PFX file' : (type === 'private' ? 'Private key' : 'Certificate');
      setSuccess(`${typeLabel} downloaded successfully`);
    } catch (err) {
      setError(err.message || 'Failed to download certificate');
    }
  };

  const handleDeleteCertificate = async (certId) => {
    if (!window.confirm('Are you sure you want to delete this certificate? This may affect SAML applications using it.')) {
      return;
    }
    
    try {
      setLoading(true);
      await samlAPI.deleteCertificate(certId);
      setSuccess('Certificate deleted successfully');
      loadCertificates();
    } catch (err) {
      setError(err.message || 'Failed to delete certificate');
    } finally {
      setLoading(false);
    }
  };

  const isExpiringSoon = (validUntil) => {
    const expiryDate = new Date(validUntil);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30;
  };

  const isExpired = (validUntil) => {
    return new Date(validUntil) < new Date();
  };

  return (
    <PageLayout
      title="SAML Certificates"
      subtitle="Manage signing certificates for SAML Identity Provider"
      backTo="/management"
      backLabel="Back to Management"
    >
      <div className="max-w-7xl mx-auto">
        {/* Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-700 font-bold">×</button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex justify-between">
            <span>{success}</span>
            <button onClick={() => setSuccess('')} className="text-green-700 font-bold">×</button>
          </div>
        )}

        {/* Generate Certificate Section */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-medium">Generate New Certificate</h2>
            <p className="text-sm text-gray-500">Create a new self-signed certificate for SAML signing</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Certificate Name *
                </label>
                <input
                  type="text"
                  value={newCert.name}
                  onChange={(e) => setNewCert({ ...newCert, name: e.target.value })}
                  placeholder="e.g., Production Certificate 2026"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Common Name (CN) *
                </label>
                <input
                  type="text"
                  value={newCert.common_name}
                  onChange={(e) => setNewCert({ ...newCert, common_name: e.target.value })}
                  placeholder="e.g., idp.backups.net.au"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization
                </label>
                <input
                  type="text"
                  value={newCert.organization}
                  onChange={(e) => setNewCert({ ...newCert, organization: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Validity (days)
                </label>
                <input
                  type="number"
                  value={newCert.validity_days}
                  onChange={(e) => setNewCert({ ...newCert, validity_days: parseInt(e.target.value) || 365 })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div className="mt-4">
              <button
                onClick={handleGenerateCertificate}
                disabled={!newCert.name || !newCert.common_name || loading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Generating...' : 'Generate Certificate'}
              </button>
            </div>
          </div>
        </div>

        {/* Existing Certificates */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-medium">Existing Certificates ({certificates.length})</h2>
            <p className="text-sm text-gray-500">Certificates available for SAML signing</p>
          </div>
          
          {loading && certificates.length === 0 ? (
            <div className="p-6 text-center text-gray-500">Loading...</div>
          ) : certificates.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-5xl mb-4">🔐</div>
              <h3 className="text-lg font-medium mb-2">No certificates found</h3>
              <p className="text-gray-500">Generate a certificate above to get started.</p>
            </div>
          ) : (
            <div className="divide-y">
              {certificates.map(cert => (
                <div key={cert.id} className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium text-lg">{cert.name}</h3>
                        {isExpired(cert.valid_until) ? (
                          <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800">Expired</span>
                        ) : isExpiringSoon(cert.valid_until) ? (
                          <span className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-800">Expiring Soon</span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800">Valid</span>
                        )}
                      </div>
                      
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500">Fingerprint:</span>
                          <code className="ml-2 text-xs bg-gray-100 px-2 py-1 rounded">{cert.fingerprint}</code>
                        </div>
                        <div>
                          <span className="text-gray-500">Algorithm:</span>
                          <span className="ml-2">{cert.algorithm}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Valid From:</span>
                          <span className="ml-2">{new Date(cert.valid_from).toLocaleDateString()}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Valid Until:</span>
                          <span className={`ml-2 ${isExpired(cert.valid_until) ? 'text-red-600' : isExpiringSoon(cert.valid_until) ? 'text-yellow-600' : ''}`}>
                            {new Date(cert.valid_until).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      {/* Download buttons */}
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          onClick={() => handleDownloadCertificate(cert.id, cert.name, 'public')}
                          className="px-3 py-1.5 text-sm text-primary hover:bg-blue-50 rounded border border-blue-300 inline-flex items-center gap-1"
                        >
                          <span>📥</span> Public Key (.pem)
                        </button>
                        <button
                          onClick={() => handleDownloadCertificate(cert.id, cert.name, 'private')}
                          className="px-3 py-1.5 text-sm text-orange-600 hover:bg-orange-50 rounded border border-orange-300 inline-flex items-center gap-1"
                        >
                          <span>🔐</span> Private Key (.pem)
                        </button>
                        <button
                          onClick={() => {
                            const password = prompt('Enter PFX password (default: changeit):', 'changeit');
                            if (password !== null) {
                              handleDownloadCertificate(cert.id, cert.name, 'pfx', password);
                            }
                          }}
                          className="px-3 py-1.5 text-sm text-purple-600 hover:bg-purple-50 rounded border border-purple-300 inline-flex items-center gap-1"
                        >
                          <span>📦</span> PFX Bundle (.pfx)
                        </button>
                      </div>
                    </div>

                    <div className="ml-4">
                      <button
                        onClick={() => handleDeleteCertificate(cert.id)}
                        className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded border border-red-300"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-primary mb-2">About SAML Certificates</h3>
          <ul className="text-sm text-primary space-y-1">
            <li>• Certificates are used to sign SAML assertions sent to Service Providers</li>
            <li>• The public key should be shared with Service Providers for signature verification</li>
            <li>• Keep private keys secure - they should never be shared</li>
            <li>• Certificates are site-wide and can be used by any SAML application</li>
          </ul>
        </div>
      </div>
    </PageLayout>
  );
};

export default CertificatesPage;
